<template>
  <div class="flex items-center gap-2 w-full">
    <el-select
      v-model="selectedCountryCode"
      class="w-20"
      filterable
      :filter-method="filterCountry"
      :teleported="false"
      popper-class="swd-phone-country-popper"
      @change="onCountryChange"
    >
      <el-option
        v-for="country in orderedCountries"
        :key="country.code"
        :label="country.dial"
        :value="country.code"
      >
        <div class="flex justify-between items-center w-full">
          <span class="flex-1 text-sm truncate">{{ country.label }}</span>
        </div>
      </el-option>
    </el-select>

    <div class="flex-1 phone-input-wrapper">
      <el-input
        v-model="localNumber"
        class="w-full"
        :placeholder="placeholder"
        inputmode="tel"
        clearable
        :class="{ 'is-error': phoneError }"
        @input="onNumberInput"
        @blur="onBlur"
        @focus="onFocus"
      />

      <p v-if="showInlineError && phoneError" class="phone-error text-red-500 text-xs">
        {{ phoneErrorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import countriesJson from '@/data/countries.json'
import { validatePhone } from '@/validationRules/fields/rules-phone' // adjust path if needed

export default {
  name: 'SwdPhoneInput',

  props: {
    modelValue: {
      type: [String, null],
      default: '',
    },
    defaultCountry: {
      type: String,
      default: 'US',
    },
    preferredCountries: {
      type: Array,
      default: () => ['US'],
    },
    required: {
      type: Boolean,
      default: true,
    },
    showInlineError: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '(XXX) XXX-XXXX',
    },
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  data() {
    // Normalise countries
    const normalizedCountries = countriesJson.map((c) => {
      const match = String(c.label).match(/^(.*)\s+\(\+\d+\)\s*$/)
      const name = match ? match[1].trim() : c.label

      return {
        code: c.code,
        dial: c.dial,
        name,
        label: c.label,
        digits: String(c.dial).replace(/[^\d]/g, ''),
      }
    })

    const countryMap = {}
    normalizedCountries.forEach((c) => {
      countryMap[c.code] = c
    })

    return {
      countries: normalizedCountries,
      countryMap,
      selectedCountryCode: 'US',
      localNumber: '',
      countrySearch: '',
      phoneError: false,
      phoneErrorMessage: '',
      hasInteracted: false,
    }
  },

  computed: {
    allowedCountries() {
      const prefSet = new Set(this.preferredCountries || [])
      const allowed = this.countries.filter((c) => prefSet.has(c.code))
      return allowed.length ? allowed : this.countries
    },

    orderedCountries() {
      const allowed = this.allowedCountries

      const q = this.countrySearch.trim().toLowerCase()
      if (!q) return allowed

      return allowed.filter((c) => {
        return (
          c.code.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q) ||
          c.label.toLowerCase().includes(q) ||
          c.dial.toLowerCase().includes(q)
        )
      })
    },

    selectedCountry() {
      return this.countryMap[this.selectedCountryCode] || this.allowedCountries[0]
    },
  },

  watch: {
    // When v-model from parent changes (edit mode, API load, etc.)
    modelValue: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.selectedCountryCode = this.defaultCountry
          this.localNumber = ''
          this.phoneError = false
          this.phoneErrorMessage = ''
          this.hasInteracted = false
          return
        }

        const rawDigits = String(val).replace(/[^\d]/g, '')

        // Robust detection: pick country with the LONGEST dial prefix that matches
        const best = this.detectCountry(rawDigits)

        let localDigits
        if (best) {
          this.selectedCountryCode = best.code
          localDigits = rawDigits.slice(best.digits.length)
        } else {
          this.selectedCountryCode = this.defaultCountry
          localDigits = rawDigits
        }

        localDigits = localDigits.slice(0, 10)
        this.localNumber = this.formatDigits(localDigits)
        this.validateLength(localDigits)
      },
    },
  },

  created() {
    this.selectedCountryCode = this.selectedCountryCode || this.defaultCountry
    const allowedSet = new Set(this.preferredCountries || [])
    if (allowedSet.size && !allowedSet.has(this.selectedCountryCode)) {
      this.selectedCountryCode = this.defaultCountry
    }
  },

  methods: {
    // --- small helper single source of truth for digits ---
    getDigitsFromLocal() {
      return (this.localNumber || '').replace(/[^\d]/g, '')
    },

    // Find country whose dial digits are the longest prefix of the raw number
    detectCountry(rawDigits) {
      let best = null
      let bestLen = 0

      for (const c of this.allowedCountries) {
        const len = c.digits.length
        if (len <= 0) continue
        if (rawDigits.startsWith(c.digits) && len > bestLen) {
          best = c
          bestLen = len
        }
      }

      return best
    },

    // Format "1234567890" -> "(123) 456-7890"
    formatDigits(digits) {
      const len = digits.length
      if (len === 0) return ""
      if (len <= 3) return `(${digits}`
      if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    },

    onNumberInput() {
      let digits = this.getDigitsFromLocal()

      // Hard cap: 10 digits
      if (digits.length > 10) digits = digits.slice(0, 10)

      this.localNumber = this.formatDigits(digits)
      this.validateLength(digits)
      this.emitValue(digits)
    },

    onFocus() {
      this.$emit('focus')
    },

    onBlur() {
      this.hasInteracted = true
      const digits = this.getDigitsFromLocal()
      this.validateLength(digits)
      this.emitValue(digits)
      this.$emit('blur')
    },

    validateLength(digits) {
      const { error, message } = validatePhone({
        digits,
        required: this.required,
        touched: this.hasInteracted,
      })

      this.phoneError = error
      this.phoneErrorMessage = message
    },

    emitValue(digits) {
      const len = digits.length
      if (!len) {
        this.phoneError = false
        this.$emit('update:modelValue', null)
        return
      }
      const prefix = this.selectedCountry?.dial || ''

      let value = ''
      if (prefix.startsWith('+')) {
        value = `${prefix}${digits}`
      } else if (prefix) {
        value = `+${prefix}${digits}`
      } else {
        value = digits
      }

      this.$emit('update:modelValue', value) // always E.164 for valid state
    },

    // When user changes the country code manually
    onCountryChange() {
      const digits = this.getDigitsFromLocal()
      this.validateLength(digits)
      this.emitValue(digits)
    },

    filterCountry(query) {
      this.countrySearch = query
    },
  },
}
</script>

<style scoped>
.swd-phone-country-popper .el-select-dropdown__item {
  padding-top: 4px;
  padding-bottom: 4px;
}

.phone-input-wrapper {
  position: relative;
}

.phone-error {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 2px;
}

/* Error border */
:deep(.el-input.is-error .el-input__wrapper) {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 1px #dc2626 inset !important;
}

/* Error border on focus */
:deep(.el-input.is-error .el-input__wrapper.is-focus) {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 1px #dc2626 inset !important;
}
</style>
